import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useParams } from 'react-router-dom';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { STATUS_COLOR_MAP } from '../../lib/utils';
import Icon from '../ds/Icon';
import { LogoMark } from '../ds/LogoMark';

export const Spinner = ({ message = 'Loading data', mode = 'big' } = {}) => {
    const small = mode === 'small';
    return (
        <div className={classNames('ds-loading', { 'is-small': small })}>
            <span className={classNames('ds-spinner', { 'is-big': !small })} />
            {message && <span>{message}</span>}
        </div>
    );
};

export const TRWrapper = (props) => (
    <tr className={props.className}><td colSpan={props.colSpan}>{props.children}</td></tr>
);

export const ButtonGroup = ({ buttons, onSelect, selected }) => (
    <div className="ds-seg" role="tablist">
        {buttons.map(b => (
            <button
                key={b}
                type="button"
                role="tab"
                aria-selected={b === selected}
                onClick={() => onSelect(b)}
                className={classNames('ds-seg__btn', { 'is-active': b === selected })}
            >
                {b}
            </button>
        ))}
    </div>
);

export const SidebarLink = (props) => (
    <NavLink
        {...props}
        className={({ isActive }) => classNames('app-nav__item', { 'is-active': isActive })}
    >
        {props.children}
    </NavLink>
);

export const CopLogin = () => {
    const [url, setUrl] = useState('zcalo://');
    useEffect(() => {
        const qs = new URLSearchParams();
        qs.set('token', localStorage.getItem('access_token'));
        qs.set('server', document.location.origin);
        const u = `zcalo://login/?${qs.toString()}`;
        setUrl(u);
        const t = setTimeout(() => { window.location.href = u; }, 1000);
        return () => clearTimeout(t);
    }, [1]);
    return (
        <div className="ds-scope ds-empty-page">
            <a className="ds-btn is-primary" href={url}>Click here to login to CGS Copilot</a>
        </div>
    );
};

export const NotFound = () => (
    <div className="ds-scope ds-empty-page">
        <h1>404</h1>
        <p style={{ color: 'var(--ds-ink-muted)' }}>The page you’re looking for doesn’t exist.</p>
        <Link className="ds-btn is-soft" to="/">Go home</Link>
    </div>
);

export const LurkerHeader = () => (
    <div className="ds-scope" style={{ padding: 'var(--ds-space-4) var(--ds-space-6)', borderBottom: '1px solid var(--ds-border)', background: 'var(--ds-surface)' }}>
        <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', textDecoration: 'none' }}>
            <LogoMark size={30} />
            <span style={{ fontFamily: 'var(--ds-font-display)', fontWeight: 700, fontSize: 'var(--ds-text-xl)', color: 'var(--ds-ink)', letterSpacing: '-0.02em' }}>Focuz</span>
        </Link>
    </div>
);

export const Footer = () => (
    <footer className="ds-scope ds-footer">
        <Link to={`/pages?path=faq`}>FAQ</Link>
        <Link to={`/pages?path=download`}>Download</Link>
        <Link to={`/pages?path=terms`}>Terms of Service</Link>
        <Link to={`/pages?path=privacy`}>Privacy</Link>
        <Link to={`/login`}>Home</Link>
    </footer>
);

export const TabFilter = (props) => {
    const { name, count, basePath = document.location.pathname, filters = [], mode = 'query' } = props;
    const qs = new URLSearchParams(document.location.search);
    const { tab } = useParams();

    let current = qs.get(name);
    if (mode === 'path') {
        current = tab;
    }
    if (!current && filters.length > 0) {
        current = filters[0].toLowerCase();
    }

    return (
        <div className="ds-tabs">
            {filters?.map(item => {
                const isActive = current === item.toLowerCase();
                const parts = basePath.split('/');
                if (mode === 'path') {
                    parts.push(item.toLowerCase());
                } else {
                    qs.set(name, item.toLowerCase());
                }
                return (
                    <Link
                        key={item}
                        className={classNames('ds-tab', { 'is-active': isActive })}
                        to={`${parts.join('/')}?${qs.toString()}`}
                    >
                        {item}
                        {isActive && count > 0 && <span className="ds-tab__count">{count}</span>}
                    </Link>
                );
            })}
        </div>
    );
};

// Accessible dialog: ESC to close, click-outside to close, focus
// moves into the dialog on open and restores on close (fixes audit C3).
export const ModalManager = ({ modalName, children, className, title }) => {
    const modalData = useSelector(st => st.modalStatus[modalName]);
    const dispatch = useDispatch();
    const dialogRef = useRef(null);
    const open = Boolean(modalData);

    const hideModal = () => dispatch({ type: 'hide-modal', name: modalName });

    useEffect(() => {
        if (!open) {
            return undefined;
        }
        const prevFocus = document.activeElement;
        const onKey = (e) => { if (e.key === 'Escape') hideModal(); };
        document.addEventListener('keydown', onKey);
        dialogRef.current?.focus();
        return () => {
            document.removeEventListener('keydown', onKey);
            if (prevFocus && typeof prevFocus.focus === 'function') {
                prevFocus.focus();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    return (
        <div
            className={classNames('ds-scope ds-modal', { 'is-open': open })}
            onMouseDown={(e) => { if (e.target === e.currentTarget) hideModal(); }}
        >
            <div
                ref={dialogRef}
                tabIndex={-1}
                role="dialog"
                aria-modal="true"
                aria-label={title || modalName}
                className={classNames('ds-modal__dialog', className)}
                {...(open ? {} : { inert: '' })}
            >
                <button
                    type="button"
                    className="ds-btn is-ghost is-icon ds-modal__close"
                    aria-label="Close dialog"
                    onClick={hideModal}
                >
                    <Icon name="close" />
                </button>
                {children}
            </div>
        </div>
    );
};

// Accessible yes/no confirm dialog (local-state driven, takes callbacks).
export const ConfirmDialog = ({ open, title, message, confirmLabel = 'Confirm', cancelLabel = 'Cancel', danger, onConfirm, onCancel }) => {
    const dialogRef = useRef(null);
    useEffect(() => {
        if (!open) {
            return undefined;
        }
        const prevFocus = document.activeElement;
        const onKey = (e) => { if (e.key === 'Escape') onCancel(); };
        document.addEventListener('keydown', onKey);
        dialogRef.current?.focus();
        return () => {
            document.removeEventListener('keydown', onKey);
            if (prevFocus && typeof prevFocus.focus === 'function') {
                prevFocus.focus();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    if (!open) {
        return null;
    }
    return (
        <div className="ds-scope ds-modal is-open" onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
            <div ref={dialogRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label={title}
                className="ds-modal__dialog" style={{ maxWidth: '420px' }}>
                <h3 style={{ marginBottom: 'var(--ds-space-2)' }}>{title}</h3>
                {message && <p style={{ color: 'var(--ds-ink-soft)' }}>{message}</p>}
                <div className="ds-form-actions" style={{ marginTop: 'var(--ds-space-5)' }}>
                    <button type="button" className="ds-btn is-ghost" onClick={onCancel}>{cancelLabel}</button>
                    <button type="button" className={`ds-btn ${danger ? 'is-danger' : 'is-primary'}`} onClick={onConfirm}>{confirmLabel}</button>
                </div>
            </div>
        </div>
    );
};

export const AlertModal = () => {
    const data = useSelector(st => st.modalStatus['alertModal']);
    return (
        <ModalManager modalName="alertModal" title={data?.title || 'Alert'}>
            {data && (
                <div>
                    <h3 style={{ marginBottom: 'var(--ds-space-3)' }}>{data.title}</h3>
                    <div className={data.msgCls}>{data.message}</div>
                </div>
            )}
        </ModalManager>
    );
};

// Checkout-type statuses shown as a color dot (+ label on hover) when compact.
const DOT_STATUS = {
    'Checked Out': 'var(--ds-danger)',
    'Check Out': 'var(--ds-danger)',
    'Auto Checkout': 'var(--ds-warning)',
    'Inactive Check Out': 'var(--ds-ink-faint)',
};

export const StatusLabel = ({ info, compact }) => {
    const status = info.status;
    const when = moment.unix(info.statusTime / 1000).fromNow();

    if (compact && DOT_STATUS[status]) {
        return (
            <span className="status-dot" tabIndex={0} title={`${status} · ${when}`}>
                <span className="status-dot__mark" style={{ background: DOT_STATUS[status] }} />
                <span className="status-dot__pop">{status} · {when}</span>
            </span>
        );
    }

    return (
        <span
            className={classNames('ds-badge progress-label', STATUS_COLOR_MAP[status])}
            title={when}
        >
            {status}
        </span>
    );
};

export const LocationErrorModal = () => {
    const message = useSelector(st => st.modalStatus['locationError']);
    return (
        <ModalManager modalName="locationError" title="Location permission required">
            <div style={{ textAlign: 'center' }}>
                <h3 style={{ marginBottom: 'var(--ds-space-5)' }}>{message}</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 'var(--ds-space-3)' }}>
                    <img style={{ width: '100%', borderRadius: 'var(--ds-radius)' }} alt="permission off"
                        src="https://cgs-cdn.sgp1.cdn.digitaloceanspaces.com/zcalo/img/loc-on.jpg?v=1" />
                    <img style={{ width: '100%', borderRadius: 'var(--ds-radius)' }} alt="permission start"
                        src="https://cgs-cdn.sgp1.cdn.digitaloceanspaces.com/zcalo/img/loc-off.jpg?v=1" />
                    <img style={{ width: '100%', borderRadius: 'var(--ds-radius)' }} alt="reload and give permission"
                        src="https://cgs-cdn.sgp1.cdn.digitaloceanspaces.com/zcalo/img/loc-accept.jpg?v=1" />
                </div>
            </div>
        </ModalManager>
    );
};
